import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import UserContext from "../../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import authenticate from "../../utils/Authenticate";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { isAdminRole } from "../../utils/roles";
import { n2 } from "../../utils/fmt";
import displayError from "../../utils/ErrorHandler";

const SendToZone = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [hasGeneratedRawMaterials, setHasGeneratedRawMaterials] =
    useState(false);

  // State for zones and stock
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState("");
  const [loadingZones, setLoadingZones] = useState(true);

  // State for menu selection and details
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState("");
  const [menuDetails, setMenuDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedMenus, setSelectedMenus] = useState([
    { id: "", details: null, quantities: {} },
  ]);
  // Yield related states (mirroring ZoneMenuCreation logic)
  const [menuYieldApplied, setMenuYieldApplied] = useState({}); // { index: 'admin' | 'central' }
  const [menuYieldSaveStatus, setMenuYieldSaveStatus] = useState({}); // { index: 'success' | 'error' }
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [totalThaal, setTotalThaal] = useState(0);
  const [submittedFormData, setSubmittedFormData] = useState([]);

  // State for consolidated raw materials management
  const [consolidatedRawMaterials, setConsolidatedRawMaterials] = useState([]);
  const [showRawMaterials, setShowRawMaterials] = useState(false);

  // State for the global "Add Item" functionality
  const [showAddItem, setShowAddItem] = useState(false);
  const [addItemSearchTerm, setAddItemSearchTerm] = useState("");
  const [addItemSearchResults, setAddItemSearchResults] = useState([]);
  const [selectedNewItem, setSelectedNewItem] = useState(null);
  const [newItemQuantity, setNewItemQuantity] = useState("");

  // NEW: Loading states for async operations
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // NEW: State to hold available central stock
  const [centralStock, setCentralStock] = useState([]);

  // Slip-image extraction state (same AI-assisted slip upload as Item Out / stockout)
  const [slipUploading, setSlipUploading] = useState(false);
  const [slipError, setSlipError] = useState('');
  const [unmatchedFromSlip, setUnmatchedFromSlip] = useState([]);
  const [uncertainItemKeys, setUncertainItemKeys] = useState(new Set());

  // Fetch Central Stock on component mount for "Add Item" search
  useEffect(() => {
    const fetchCentralStock = async () => {
      try {
        const stockResponse = await axios.get(
          `${process.env.REACT_APP_API_URI}/api/stock/get?id=${user.zone._id}`,
          { withCredentials: true }
        );
        setCentralStock(stockResponse.data.data);
      } catch (error) {
        console.error("Error fetching central stock:", error);
        setNotification({
          message: "Could not load stock data for adding new items.",
          type: "error",
        });
      }
    };
    fetchCentralStock();
  }, []);

  const handleExportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [];

    wsData.push(["Order Summary"]);
    submittedFormData.forEach((menuBlock) => {
      wsData.push([`Menu: ${menuBlock.menuName}`]);
      menuBlock.dishes.forEach((dish) => {
        wsData.push([
          `  - ${dish.dishName}`,
          `${n2(dish.quantity)} ${dish.unit}`,
          `(Per Thaal: ${dish.perThaalQuantity != null ? n2(dish.perThaalQuantity) : "N/A"})`,
        ]);
      });
    });
    wsData.push([]);
    wsData.push([`Total Thaal: ${totalThaal}`]);
    wsData.push([]);

    wsData.push(["Consolidated Raw Materials"]);
    wsData.push(["Material Name", "Quantity", "Unit"]);
    consolidatedRawMaterials.forEach((material) => {
      wsData.push([
        material.dish.name,
        n2(material.quantity),
        material.dish.unit,
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }];

    XLSX.utils.book_append_sheet(wb, ws, "Required Materials");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, "Required_Raw_Materials.xlsx");
  };

  const handleConvertPo = () => {
    const zoneInfo = zones.find((z) => z._id === selectedZone);
    navigate("/central/create-po", {
      state: {
        rawMaterials: consolidatedRawMaterials,
        zone: { desc: zoneInfo?.desc || null, id: zoneInfo?._id || null },
      },
    });
  };

  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoadingZones(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URI}/api/zones/getAll`,
          { withCredentials: true }
        );
        setZones(response.data.data);
      } catch (error) {
        console.error("Error fetching zones:", error);
        setNotification({ message: "Error fetching zones", type: "error" });
      } finally {
        setLoadingZones(false);
      }
    };
    fetchZones();
  }, []);

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URI}/api/menu/getAll`,
          { withCredentials: true }
        );
        setMenus(response.data.data);
      } catch (error) {
        console.error("Error fetching menus:", error);
        setNotification({ message: "Error fetching menus", type: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchMenus();
  }, []);

  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    }
    startup();
  }, [user, navigate, setUser]);

  const handleMenuChange = async (index, menuId) => {
    const updatedMenus = [...selectedMenus];
    // Reset menu entry
    updatedMenus[index] = { id: menuId, details: null, quantities: {} };

    // Clear yield status for this index
    setMenuYieldApplied((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setMenuYieldSaveStatus((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });

    // Reset global states impacted by menu change
    setNotification({ message: "", type: "" });
    setShowRawMaterials(false);
    setConsolidatedRawMaterials([]);
    setSubmittedFormData([]);

    const selectedMenuObj = menus.find((m) => m._id === menuId);
    if (menuId && selectedMenuObj) {
      try {
        setLoadingDetails(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URI}/api/dish/${menuId}/details`,
          { withCredentials: true }
        );
        const dishData = response.data.data;
        updatedMenus[index].details = dishData;
        const initialQuantities = {};
        dishData.dishes.forEach((dishItem) => {
          initialQuantities[dishItem._id] = "";
        });
        updatedMenus[index].quantities = initialQuantities;

        // Attempt to fetch existing yield (public GET) and prefill quantities
        try {
          // Fetch yield WITH credentials (route currently requires JWT)
          const yieldResp = await axios.get(
            `${process.env.REACT_APP_API_URI}/api/menuYield/${menuId}`,
            { withCredentials: true }
          );
          const yieldData = yieldResp.data?.data;
          if (yieldData?.dishes?.length) {
            // Normalize ids to strings for matching
            yieldData.dishes.forEach((yd) => {
              const ydDishId = typeof yd.dish === 'object' ? yd.dish._id : yd.dish;
              const match = dishData.dishes.find((d) => d?.dish?._id === ydDishId);
              if (match) {
                updatedMenus[index].quantities[match._id] = yd.quantity;
              }
            });
            setMenuYieldApplied((prev) => ({
              ...prev,
              [index]: isAdminRole(user?.role) ? 'admin' : 'central',
            }));
          } else {
            console.debug('[Yield] No yield data for menu', menuId);
          }
        } catch (yErr) {
          const status = yErr?.response?.status;
            console.warn('[Yield] fetch failed', { menuId, status, data: yErr?.response?.data });
        }
      } catch (err) {
        console.error("Error fetching menu details:", err);
        setNotification({ message: "Error fetching menu details", type: "error" });
      } finally {
        setLoadingDetails(false);
      }
    }
    setSelectedMenus(updatedMenus);
  };

  const handleAddMenu = () => {
    setSelectedMenus([...selectedMenus, { id: "", details: null, quantities: {} }]);
  };

  const handleRemoveMenu = (index) => {
    const updated = [...selectedMenus];
    updated.splice(index, 1);
    setSelectedMenus(updated);
  };

  const handleConsolidatedQuantityChange = (index, quantity) => {
    setConsolidatedRawMaterials((prev) => {
      const updated = [...prev];
      updated[index].quantity = parseFloat(quantity) || 0;
      return updated;
    });
  };

  const handleRemoveConsolidatedMaterial = (index) => {
    setConsolidatedRawMaterials((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSearchItems = (searchValue) => {
    setAddItemSearchTerm(searchValue);
    if (!searchValue) {
      setAddItemSearchResults([]);
      return;
    }
    const searchLower = searchValue.toLowerCase();
    const results = centralStock.filter((stockItem) =>
      stockItem.item.name.toLowerCase().includes(searchLower)
    );
    setAddItemSearchResults(results);
  };

  const handleSelectItem = (stockItem) => {
    setSelectedNewItem(stockItem.item);
    setAddItemSearchTerm(stockItem.item.name);
    setAddItemSearchResults([]);
  };

  const handleCancelAddItem = () => {
    setShowAddItem(false);
    setAddItemSearchTerm("");
    setAddItemSearchResults([]);
    setSelectedNewItem(null);
    setNewItemQuantity("");
  };

  const handleAddNewItem = () => {
    if (!selectedNewItem) {
      setNotification({ message: "Please select an item first", type: "error" });
      return;
    }
    if (!newItemQuantity || newItemQuantity <= 0) {
      setNotification({ message: "Please enter a valid quantity", type: "error" });
      return;
    }
    const exists = consolidatedRawMaterials.some(
      (material) => material.dish._id === selectedNewItem._id
    );
    if (exists) {
      setNotification({ message: "Item already exists. Please edit the existing item's quantity.", type: "error" });
      return;
    }
    const newMaterial = {
      dish: {
        _id: selectedNewItem._id,
        name: selectedNewItem.name,
        unit: selectedNewItem.unit,
      },
      quantity: parseFloat(newItemQuantity),
    };
    setConsolidatedRawMaterials((prev) => [...prev, newMaterial]);
    handleCancelAddItem();
  };

  // Same AI-assisted slip upload as Item Out / stockout: photograph a written
  // raw-materials slip and pre-fill the Consolidated Raw Materials list from
  // it, grounded against the central zone's own current stock (?id=user.zone._id
  // - the same endpoint, just pointed at a different zone's stock).
  const handleSlipImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setSlipUploading(true);
    setSlipError('');
    setUnmatchedFromSlip([]);

    try {
      const formData = new FormData();
      formData.append('slip', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/stock/extractItemOutFromImage?id=${user.zone._id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true }
      );

      const data = response.data.data;
      if (typeof data.thaal === 'number') setTotalThaal(data.thaal);

      const newUncertainKeys = new Set(uncertainItemKeys);

      setConsolidatedRawMaterials((prev) => {
        const updated = [...prev];

        (data.items || []).forEach((extractedItem) => {
          if (!extractedItem.quantity) return; // couldn't read a usable quantity, leave for manual entry

          const existingIndex = updated.findIndex((m) => m.dish._id === extractedItem.itemId);
          if (existingIndex >= 0) {
            // Keep any existing definedQuantity (the menu-calculated baseline) so the
            // defined-vs-updated indicator still shows this as changed if it differs.
            updated[existingIndex] = { ...updated[existingIndex], quantity: extractedItem.quantity };
          } else {
            updated.push({
              dish: { _id: extractedItem.itemId, name: extractedItem.name, unit: extractedItem.unit },
              quantity: extractedItem.quantity,
            });
          }

          if (extractedItem.uncertain) newUncertainKeys.add(extractedItem.itemId);
          else newUncertainKeys.delete(extractedItem.itemId);
        });

        return updated;
      });

      setUncertainItemKeys(newUncertainKeys);
      setUnmatchedFromSlip(data.unmatched || []);
    } catch (err) {
      const errorMessage = displayError(err, navigate, setUser);
      setSlipError(errorMessage || 'Could not read the slip image — please add items manually.');
    } finally {
      setSlipUploading(false);
    }
  };

  const handleSendToZone = async () => {
    if (consolidatedRawMaterials.some((m) => !m.quantity || m.quantity <= 0)) {
      setNotification({ message: "Please enter valid quantities for all raw materials", type: "error" });
      return;
    }
    setIsSending(true);
    try {
      const finalData = {
        menuId: submittedFormData[0]?.menuId || selectedMenu,
        menuName: submittedFormData[0]?.menuName || menuDetails?.name,
        thaalCount: totalThaal,
        rawMaterials: consolidatedRawMaterials,
        dishDetails: submittedFormData.flatMap((menu) => menu.dishes),
        user: user,
        menuStructure: consolidatedRawMaterials,
        zone: selectedZone,
        source: "central",
      };
      const createRequestResponse = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/request/menu`,
        finalData,
        { withCredentials: true }
      );
      const newRequestId = createRequestResponse.data.data._id;
      const updateStatusPayload = consolidatedRawMaterials.map((material) => ({
        requestId: newRequestId,
        itemId: material.dish._id,
        quantity: material.quantity,
      }));
      await axios.patch(
        `${process.env.REACT_APP_API_URI}/api/request/itemsReceived?id=${selectedZone}`,
        { updateStatus: updateStatusPayload },
        { withCredentials: true }
      );
      const itemsForStock = consolidatedRawMaterials.map((material) => ({
        itemId: material.dish._id,
        name: material.dish.name,
        quantity: material.quantity,
      }));
      await axios.post(
        `${process.env.REACT_APP_API_URI}/api/stock/add?id=${selectedZone}`,
        { items: itemsForStock },
        { withCredentials: true }
      );
      const stockOutPayload = {
        items: consolidatedRawMaterials.map((material) => ({ itemId: material.dish._id, name: material.dish.name, quantity: material.quantity })),
        menu: submittedFormData[0]?.menuId,
        thaal: totalThaal,
      };
      // Explicitly identify the source (central) zone to subtract from - matches
      // the explicit ?id= already used for /stock/add above, instead of relying
      // on the backend's req.user.zone fallback.
      await axios.post(
        `${process.env.REACT_APP_API_URI}/api/stock/remove?id=${user.zone._id}`,
        stockOutPayload,
        { withCredentials: true }
      );
      setNotification({ message: "Zone Menu created and stock transferred successfully!", type: "success" });
      setSelectedMenu("");
      setMenuDetails(null);
      setTotalThaal(0);
      setConsolidatedRawMaterials([]);
      setShowRawMaterials(false);
      setSubmittedFormData([]);
      setSelectedMenus([{ id: "", details: null, quantities: {} }]);
      setHasGeneratedRawMaterials(false);
      handleCancelAddItem();
    } catch (error) {
      console.error("Error in 'Send to Zone' transaction:", error);
      // Surface the backend's actual reason instead of axios's generic
      // "Request failed with status code 400" - critical here since this
      // transaction has several sequential steps (create request, mark
      // received, stock in, stock out) and the generic message gave no clue
      // which one failed or why.
      const errorMessage =
        error.response?.status === 409
          ? "Zone Menu already exists for this configuration."
          : displayError(error, navigate, setUser) || `An error occurred: ${error.message}. Please verify system state.`;
      if (errorMessage) {
        setNotification({ message: errorMessage, type: "error" });
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedZone) {
      setNotification({ message: "Please select a zone first", type: "error" });
      return;
    }
    if (selectedMenus.some((menu) => Object.values(menu.quantities).some((qty) => !qty || qty <= 0))) {
      setNotification({ message: "Please enter valid quantities for all dishes", type: "error" });
      return;
    }
    if (!totalThaal || totalThaal <= 0) {
      setNotification({ message: "Please enter valid total thaal count", type: "error" });
      return;
    }
    setIsGenerating(true);
    try {
      const menusPayload = selectedMenus
        .map((menu) => {
          if (!menu.details) return null;
          return {
            menuId: menu.id,
            menuName: menu.details.name,
            dishes: menu.details.dishes.map((dishItem) => ({
              dishItemId: dishItem._id,
              dishId: dishItem.dish._id,
              dishName: dishItem.dish.name,
              unit: dishItem.dish.unit,
              quantity: parseFloat(menu.quantities[dishItem._id]) || 0,
              perThaalQuantity: dishItem.perThaalQuantity,
            })),
          };
        })
        .filter(Boolean);
      const payload = { menus: menusPayload, thaalCount: totalThaal };
      setSubmittedFormData(menusPayload);
      const requiredMaterialsResponse = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/menu/calculateQuantity`,
        payload,
        { withCredentials: true }
      );
      const requiredMaterials = requiredMaterialsResponse.data.data;
      const availableItemIds = new Set(centralStock.map((stockItem) => stockItem.item._id));
      const filteredMaterials = requiredMaterials
        .map((menuBlock) => ({
          ...menuBlock,
          dishes: menuBlock.dishes
            .map((dishBlock) => ({
              ...dishBlock,
              item: dishBlock.item.filter((material) => availableItemIds.has(material.dish._id)),
            }))
            .filter((dishBlock) => dishBlock.item.length > 0),
        }))
        .filter((menuBlock) => menuBlock.dishes.length > 0);
      if (filteredMaterials.length === 0) {
        setNotification({ message: "No required raw materials are available in the central stock.", type: "error" });
        return;
      }
      const materialMap = new Map();
      filteredMaterials.forEach((menuBlock) => {
        menuBlock.dishes.forEach((dishBlock) => {
          dishBlock.item.forEach((material) => {
            const existing = materialMap.get(material.dish._id);
            if (existing) {
              existing.quantity += material.quantity;
              existing.definedQuantity += material.quantity;
            } else {
              // definedQuantity is the system-calculated baseline, kept immutable
              // so the UI can always show what was originally calculated even
              // after the user edits `quantity`.
              materialMap.set(material.dish._id, { dish: material.dish, quantity: material.quantity, definedQuantity: material.quantity });
            }
          });
        });
      });
      setNotification({ message: "Raw Materials Generated Successfully", type: "success" });
      setConsolidatedRawMaterials(Array.from(materialMap.values()));
      setShowRawMaterials(true);
      setHasGeneratedRawMaterials(true);
    } catch (error) {
      console.error("Error generating raw materials:", error);
      const errorMessage = displayError(error, navigate, setUser) || "An error occurred while generating raw materials";
      if (errorMessage) {
        setNotification({ message: errorMessage, type: "error" });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Admin-only: save canonical menu yield before generation (per menu index)
  const handleSaveMenuYield = async (menuIndex) => {
    if (hasGeneratedRawMaterials) return; // Prevent after generation
    if (!isAdminRole(user?.role)) return; // Only admins

    const targetMenu = selectedMenus[menuIndex];
    if (!targetMenu?.id || !targetMenu?.details) {
      setNotification({ message: 'Select a menu first', type: 'error' });
      return;
    }

    const dishEntries = targetMenu.details.dishes.map(dishItem => ({
      dishId: dishItem.dish._id,
      quantity: parseFloat(targetMenu.quantities[dishItem._id]) || 0,
    }));

    const invalid = dishEntries.some(d => !d.quantity || d.quantity <= 0);
    if (invalid) {
      setNotification({ message: 'Enter valid quantities for all dishes before saving yield', type: 'error' });
      return;
    }

    const payload = { menuId: targetMenu.id, dishes: dishEntries, source: 'Central Admin' };
    try {
      await axios.post(`${process.env.REACT_APP_API_URI}/api/menuYield/save`, payload, { withCredentials: true });
      setMenuYieldSaveStatus(prev => ({ ...prev, [menuIndex]: 'success' }));
      setMenuYieldApplied(prev => ({ ...prev, [menuIndex]: 'admin' }));
    } catch (error) {
      console.error('Error saving menu yield', error);
      setMenuYieldSaveStatus(prev => ({ ...prev, [menuIndex]: 'error' }));
    }
  };

  return (
    <div className="wrapper">
      <div className="w-full">
        <h2 className="text-base font-bold bg-lime-800 text-white p-3 py-4 text-center">
          Zone Menu Creation
        </h2>
        {notification.message && (
          <div className={`p-2 mb-4 ${notification.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
            {notification.message}
          </div>
        )}
      </div>

      <div className={`max-w-4xl mx-auto transition-filter duration-300 ${isGenerating || isSending ? "filter blur-sm pointer-events-none" : ""}`}>
        <form onSubmit={handleSubmit} className="px-8 pt-6 pb-8 mb-4">
          {!hasGeneratedRawMaterials && (
            <>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="zone-select">Select Zone *</label>
                <select id="zone-select" value={selectedZone} onChange={(e) => setSelectedZone(e.target.value)} className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" required>
                  <option value="">Choose a zone</option>
                  {loadingZones ? <option disabled>Loading zones...</option> : zones.map((zone) => <option key={zone._id} value={zone._id}>{zone.desc}</option>)}
                </select>
              </div>

              <div className="mb-6"><label className="block text-gray-700 text-sm font-bold mb-2">Select Menu(s)</label></div>
              {loadingDetails && <div className="text-center py-4"><p className="text-gray-600">Loading menu details...</p></div>}
              {selectedMenus.map((menu, index) => (
                <div key={index} className="mb-6 border p-4 rounded-lg">
                  <div className="flex items-center gap-4 mb-4">
                    <select value={menu.id} onChange={(e) => handleMenuChange(index, e.target.value)} className="shadow border rounded px-3 py-2 w-full disabled:bg-gray-200" disabled={!selectedZone}>
                      <option value="">{selectedZone ? "Choose a menu" : "Please select a zone first"}</option>
                      {menus.filter((m) => !selectedMenus.some((sm, i) => sm.id === m._id && i !== index)).map((m) => <option key={m._id} value={m._id}>{m.name}</option>)}
                    </select>
                    {selectedMenus.length > 1 && <button onClick={() => handleRemoveMenu(index)} type="button" className="bg-red-500 text-white px-3 py-1 rounded">Remove</button>}
                  </div>
                  {menuYieldApplied[index] && (
                    <div className="mb-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded">
                      {menuYieldApplied[index] === 'admin' ? 'Previously saved yield loaded' : 'Standard yield applied (From Central Admin)'}
                    </div>
                  )}
                  {menuYieldSaveStatus[index] === 'success' && (
                    <div className="mb-2 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded">
                      Menu Yield saved successfully
                    </div>
                  )}
                  {menuYieldSaveStatus[index] === 'error' && (
                    <div className="mb-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">
                      Failed to save Menu Yield
                    </div>
                  )}
                  {menu.details && menu.details.dishes.map((dish) => (
                    <div key={dish._id} className="grid grid-cols-3 gap-4 mb-3">
                      <input readOnly value={dish.dish.name} className="bg-gray-100 border px-2 py-1 rounded" />
                      <input type="number" placeholder="Qty" value={menu.quantities[dish._id] || ""} onChange={(e) => { const updated = [...selectedMenus]; updated[index].quantities[dish._id] = e.target.value; setSelectedMenus(updated); }} onWheel={(e) => e.target.blur()} className="border px-2 py-1 rounded" />
                      <input readOnly value={dish.dish.unit} className="bg-gray-100 border px-2 py-1 rounded" />
                    </div>
                  ))}
                  {!hasGeneratedRawMaterials && isAdminRole(user?.role) && menu.details && (
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleSaveMenuYield(index)}
                        className="bg-blue-600 hover:bg-blue-800 text-white font-semibold py-2 px-5 rounded-full text-sm transition-colors"
                      >
                        Save Menu Yield
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <button type="button" onClick={handleAddMenu} className="bg-green-600 text-white px-4 py-2 rounded mb-6">+ Add Menu</button>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">Total Thaal *</label>
                <input type="number" value={totalThaal} onChange={(e) => setTotalThaal(e.target.value)} onWheel={(e) => e.target.blur()} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" required min="1" />
              </div>

              <div className="flex items-center justify-center mt-8">
                <button type="submit" className="bg-fmb text-white font-bold py-2 px-6 rounded-full focus:outline-none focus:shadow-outline hover:bg-opacity-90 transition-colors w-72" disabled={isGenerating}>
                  {isGenerating ? "Generating..." : "Generate Required Raw Materials"}
                </button>
              </div>
            </>
          )}

          {showRawMaterials && (
            <>
              <div className="mb-6 p-4 border border-blue-300 rounded-lg bg-blue-50">
                <h3 className="text-lg font-bold text-blue-800 mb-3">Order Summary</h3>
                {submittedFormData.map((menu, idx) => (
                  <div key={idx} className="mb-4 border-b pb-2">
                    <p><span className="font-semibold">Menu:</span> {menu.menuName}</p>
                    <ul className="ml-4 list-disc">{menu.dishes.map((dish, index) => <li key={index}>{dish.dishName} - {dish.quantity} {dish.unit} (Per Thaal: {dish.perThaalQuantity || "N/A"})</li>)}</ul>
                  </div>
                ))}
                <p className="mt-2 font-semibold">Total Thaal: {totalThaal}</p>
              </div>

              {/* Upload Slip Image - same AI-assisted extraction as Item Out / stockout */}
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Upload Slip Image (optional)
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Already wrote out the raw-materials slip? Photograph it here and we'll try to pre-fill the quantities below — review and correct before sending.
                </p>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleSlipImageUpload}
                  disabled={slipUploading}
                  className="text-sm block w-full border bg-white border-gray-300 rounded-md p-2"
                />
                {slipUploading && <p className="text-xs text-blue-600 mt-1">Reading slip image...</p>}
                {slipError && <p className="text-xs text-red-600 mt-1">{slipError}</p>}
                {unmatchedFromSlip.length > 0 && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-xs font-semibold text-amber-800 mb-1">
                      Couldn't match these rows from the slip to central stock — add them manually if needed:
                    </p>
                    <ul className="text-xs text-amber-700 list-disc list-inside">
                      {unmatchedFromSlip.map((row, idx) => (
                        <li key={idx}>{row.rawName}{row.quantity ? ` — qty ${row.quantity}` : ''}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mb-4"><h3 className="text-xl font-bold text-gray-800">Consolidated Raw Materials</h3></div>
              <div className="space-y-4 mb-6">
                {consolidatedRawMaterials.map((material, index) => {
                  const hasDefined = material.definedQuantity != null;
                  const qtyNum = parseFloat(material.quantity);
                  const isChanged = hasDefined && !isNaN(qtyNum) && Math.abs(qtyNum - material.definedQuantity) > 0.001;
                  const isUncertain = uncertainItemKeys.has(material.dish._id);
                  return (
                  <div key={material.dish._id} className={`p-3 border rounded-lg bg-white ${isUncertain ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">
                          Material Name
                          {isUncertain && (
                            <span className="ml-2 text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full align-middle">
                              Please verify
                            </span>
                          )}
                        </label>
                        <input type="text" value={material.dish.name} readOnly className="bg-gray-100 border rounded w-full py-2 px-3 text-sm" />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-1">Quantity *</label>
                        <input type="number" value={material.quantity} onChange={(e) => handleConsolidatedQuantityChange(index, e.target.value)} className={`border rounded w-full py-2 px-3 text-sm ${isChanged ? 'border-amber-400 bg-amber-50' : ''}`} step="0.01" min="0" onWheel={(e) => e.target.blur()} />
                        {hasDefined && (
                          <span className={`block text-[11px] mt-1 ${isChanged ? 'text-amber-700 font-medium' : 'text-gray-500'}`}>
                            Defined: {n2(material.definedQuantity)} {material.dish.unit}{isChanged ? ' (changed)' : ''}
                          </span>
                        )}
                      </div>
                      <div><label className="block text-gray-700 text-sm font-bold mb-1">Unit</label><input type="text" value={material.dish.unit} readOnly className="bg-gray-100 border rounded w-full py-2 px-3 text-sm" /></div>
                      <div className="flex justify-center items-end h-full pt-6"><button type="button" onClick={() => handleRemoveConsolidatedMaterial(index)} className="bg-red-500 hover:bg-red-700 text-white px-3 py-2 rounded text-sm w-full">Remove</button></div>
                    </div>
                  </div>
                  );
                })}
              </div>

              <div className="mt-4">
                {!showAddItem ? (<button type="button" onClick={() => setShowAddItem(true)} className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded">+ Add New Item from Stock</button>) : (
                  <div className="p-4 border border-green-300 rounded-lg bg-green-50">
                    <h6 className="text-md font-semibold text-green-800 mb-3">Add New Item to List</h6>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                      <div className="relative md:col-span-2">
                        <label className="block text-gray-700 text-sm font-bold mb-1">Item Name *</label>
                        <input type="text" value={addItemSearchTerm} onChange={(e) => handleSearchItems(e.target.value)} placeholder="Type to search available stock..." className="shadow border rounded w-full py-2 px-3 text-sm" />
                        {addItemSearchResults?.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-40 overflow-y-auto">
                            {addItemSearchResults.map((stockItem) => (
                              <div key={stockItem.item._id} className="p-2 hover:bg-gray-100 cursor-pointer text-sm flex justify-between" onClick={() => handleSelectItem(stockItem)}>
                                <span>{stockItem.item.name} ({stockItem.item.unit})</span>
                                <span className="text-gray-500 font-medium">Available: {stockItem.quantity}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div><label className="block text-gray-700 text-sm font-bold mb-1">Quantity *</label><input type="number" value={newItemQuantity} onChange={(e) => setNewItemQuantity(e.target.value)} placeholder="Qty" min="0" step="0.01" className="shadow border rounded w-full py-2 px-3 text-sm" /></div>
                      <div className="flex gap-2"><button type="button" onClick={handleAddNewItem} className="bg-blue-500 text-white font-bold py-2 px-4 rounded text-sm">Add</button><button type="button" onClick={handleCancelAddItem} className="bg-gray-500 text-white font-bold py-2 px-4 rounded text-sm">Cancel</button></div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-8 flex flex-col items-center gap-4">
                
                  <>
                    <button type="button" onClick={handleExportToExcel} disabled={isSending} className="bg-blue-600 hover:bg-blue-800 text-white font-bold px-8 py-2 rounded-full w-64 disabled:bg-gray-400">Export to Excel</button>

                  </>
                
                  <button type="button" onClick={handleSendToZone} disabled={isSending} className="bg-fmb text-white font-bold py-3 px-8 rounded-full focus:outline-none focus:shadow-outline hover:bg-opacity-90 transition-colors disabled:bg-gray-400">
                    {isSending ? "Processing..." : "Confirm and Send to Zone"}
                  </button>
                
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default SendToZone;